import axios from 'axios'
import i18n from 'i18next'
import { useAuthStore } from '@/stores/auth.store'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3006'

export const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor - add auth token and Accept-Language
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  if (i18n.language) {
    config.headers['Accept-Language'] = i18n.language
  }
  return config
})

// Response interceptor - handle token refresh + trial expiration
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Trial expired or subscription suspended: the API blocks writes with 403.
    // Notify the UI so it can show a blocking modal with a subscribe CTA.
    const blockCode = error.response?.data?.code
    if (error.response?.status === 403 && (blockCode === 'TRIAL_EXPIRED' || blockCode === 'SUBSCRIPTION_SUSPENDED')) {
      window.dispatchEvent(new CustomEvent('r3c:trial-expired-blocked', {
        detail: { message: error.response.data.error, code: blockCode },
      }))
      return Promise.reject(error)
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true
      
      const refreshToken = useAuthStore.getState().refreshToken
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {
            refreshToken,
          })
          
          const { accessToken, refreshToken: newRefreshToken } = response.data.data
          useAuthStore.setState({ accessToken, refreshToken: newRefreshToken })
          
          originalRequest.headers.Authorization = `Bearer ${accessToken}`
          return api(originalRequest)
        } catch (refreshError) {
          useAuthStore.getState().logout()
          window.location.href = '/login'
          return Promise.reject(refreshError)
        }
      }
    }
    
    return Promise.reject(error)
  }
)
